import React, { useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { Spin } from 'antd';
import { apiScopes } from '../../services/authConfig';
import { useUserInfo } from '../../hooks/useUserInfo';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { userInfo, isLoading, hasContentAccess } = useUserInfo();
  const navigate = useNavigate();

  // Handle authentication redirect
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect({
        scopes: apiScopes,
        redirectStartPage: window.location.pathname
      }).catch((error) => {
        console.error('Login redirect failed:', error);
      });
    }
  }, [isAuthenticated, inProgress, instance]);

  // Handle role-based access control
  useEffect(() => {
    if (isAuthenticated && !isLoading && userInfo && !hasContentAccess) {
      // User is authenticated but doesn't have content management access
      navigate('/error', { replace: true });
    }
  }, [isAuthenticated, isLoading, userInfo, hasContentAccess, navigate]);

  // Show loading spinner during authentication
  if (inProgress === InteractionStatus.Login) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: 'rgba(245, 245, 245, 1)'
      }}>
        <Spin size="large" tip="Authenticating..." />
      </div>
    );
  }

  // Show loading spinner while fetching user info
  if (isAuthenticated && isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: 'rgba(245, 245, 245, 1)'
      }}>
        <Spin size="large" tip="Loading user information..." />
      </div>
    );
  }

  // Redirect to home if not authenticated
  if (!isAuthenticated && inProgress === InteractionStatus.None) {
    return <Navigate to="/" replace />;
  }

  // Redirect to error page if authenticated but no admin access
  if (isAuthenticated && !isLoading && userInfo && !hasContentAccess) {
    return <Navigate to="/error" replace />;
  }

  // Render children if user is authenticated and has content management access
  return <>{children}</>;
};

export default AdminProtectedRoute;